import {
  IPublicClientApplication,
  InteractionStatus,
} from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { PDFExport } from "@progress/kendo-react-pdf";
import {
  filePdfIcon,
  loginIcon,
  logoutIcon,
  printIcon,
} from "@progress/kendo-svg-icons";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import classes from "./App.module.scss";
import { Zone, getData } from "./api";
import { StreetBlockInventory } from "./components/StreetBlockInventory";
import { AppConfig } from "./config";
import { login, logout } from "./msal";

export const App: FunctionComponent<{ config: AppConfig }> = ({ config }) => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  if (isAuthenticated) {
    return <AppContent msalInstance={instance} config={config} />;
  } else if (
    inProgress !== InteractionStatus.Startup &&
    inProgress !== InteractionStatus.HandleRedirect
  ) {
    return <SignInContent msalInstance={instance} config={config} />;
  } else {
    return null;
  }
};

const SignInContent: FunctionComponent<{
  msalInstance: IPublicClientApplication;
  config: AppConfig;
}> = ({ msalInstance, config }) => {
  return (
    <div className={classes.page}>
      <div className={classes.logo}>
        <img src="logo.png" alt="UMOJO" />
      </div>
      <Button
        className={`${classes.btn} ${classes.login}`}
        startIcon={<SvgIcon icon={loginIcon} />}
        themeColor="light"
        fillMode="outline"
        onClick={() => login(msalInstance, config.token)}
      >
        Log In
      </Button>
    </div>
  );
};

const AppContent: FunctionComponent<{
  msalInstance: IPublicClientApplication;
  config: AppConfig;
}> = ({ msalInstance, config }) => {
  const [data, setData] = useState<Zone[] | undefined>();
  const [options, setOptions] = useState<{
    streetName?: string;
    crossStreetStartName?: string;
    crossStreetEndName?: string;
  }>({});
  const [streetBlocks, setStreetBlocks] = useState<Zone[][]>([]);

  const pdfExportComponent = useRef<PDFExport>(null);
  const blocksComponent = useRef<HTMLDivElement>(null);

  const getDocumentTitle = () => {
    if (
      options.streetName &&
      options.crossStreetStartName &&
      options.crossStreetEndName
    ) {
      return `${options.streetName}-${options.crossStreetStartName}-${options.crossStreetEndName}`;
    }
  };

  const print = useReactToPrint({
    contentRef: blocksComponent,
    pageStyle: "@media print { @page { size: 210mm 297mm; } }",
    documentTitle: getDocumentTitle(),
  });

  const getStreetNames = () => {
    return data
      ? Array.from(new Set(data.map((x) => x.StreetName))).sort()
      : [];
  };

  const getCrossStreetStartNames = () => {
    return data && options.streetName
      ? Array.from(
          new Set(
            data
              .filter((x) => x.StreetName === options.streetName)
              .map((x) => x.CrossStreetStartName)
              .sort((a, b) => {
                if (a === "UNKNOWN") return 1;
                if (b === "UNKNOWN") return -1;

                return a.localeCompare(b);
              })
          )
        )
      : [];
  };

  const getCrossStreetEndNames = () => {
    return data && options.streetName && options.crossStreetStartName
      ? Array.from(
          new Set(
            data
              .filter(
                (x) =>
                  x.StreetName === options.streetName &&
                  x.CrossStreetStartName === options.crossStreetStartName
              )
              .map((x) => x.CrossStreetEndName)
              .sort((a, b) => {
                if (a === "UNKNOWN") return 1;
                if (b === "UNKNOWN") return -1;

                return a.localeCompare(b);
              })
          )
        )
      : [];
  };

  const getUniqueZoneKeys = (
    zones: Zone[]
  ): { OSMEdges: string; StreetRightSide: boolean }[] => {
    const found = new Set<string>();

    return zones.filter((x) => {
      const key = `${x.OSMEdges}|${x.StreetRightSide}`;
      if (found.has(key)) {
        return false;
      }
      found.add(key);
      return true;
    });
  };

  useEffect(() => {
    const abortController = new AbortController();

    getData(msalInstance, config.token, abortController.signal)
      .then((data) => setData(data))
      .catch((error) => console.error(error));

    return () => abortController.abort();
  }, [msalInstance, config.token]);

  useEffect(() => {
    if (
      data &&
      options.streetName &&
      options.crossStreetStartName &&
      options.crossStreetEndName
    ) {
      const zones = data.filter(
        (x) =>
          x.StreetName === options.streetName &&
          x.CrossStreetStartName === options.crossStreetStartName &&
          x.CrossStreetEndName === options.crossStreetEndName
      );
      const zoneKeys = getUniqueZoneKeys(zones);
      const streetBlocks = zoneKeys
        .sort((a, b) => {
          if (a.StreetRightSide !== b.StreetRightSide) {
            return a.StreetRightSide ? -1 : 1;
          }

          return a.OSMEdges.localeCompare(b.OSMEdges);
        })
        .map((x) =>
          zones
            .filter(
              (y) =>
                y.OSMEdges === x.OSMEdges &&
                y.StreetRightSide === x.StreetRightSide
            )
            .sort((a, b) => a.StreetStartOffset - b.StreetStartOffset)
        );

      setStreetBlocks(streetBlocks);
    } else {
      setStreetBlocks([]);
    }
  }, [data, options]);

  return data ? (
    <>
      <div className={classes.menu}>
        <label>Street:</label>
        <DropDownList
          className={classes.dropdownlist}
          data={getStreetNames()}
          value={options.streetName || null}
          onChange={(e) => setOptions({ streetName: e.target.value })}
        />

        <label>Cross Street Start:</label>
        <DropDownList
          className={classes.dropdownlist}
          data={getCrossStreetStartNames()}
          value={options.crossStreetStartName || null}
          onChange={(e) =>
            setOptions({
              streetName: options.streetName,
              crossStreetStartName: e.target.value,
            })
          }
        />

        <label>Cross Street End:</label>
        <DropDownList
          className={classes.dropdownlist}
          data={getCrossStreetEndNames()}
          value={options.crossStreetEndName || null}
          onChange={(e) =>
            setOptions({ ...options, crossStreetEndName: e.target.value })
          }
        />

        <Button
          className={classes.btn}
          startIcon={<SvgIcon icon={printIcon} />}
          onClick={() => print()}
          disabled={
            !(
              options.streetName &&
              options.crossStreetStartName &&
              options.crossStreetEndName
            )
          }
        >
          Print
        </Button>
        <Button
          className={classes.btn}
          startIcon={<SvgIcon icon={filePdfIcon} />}
          onClick={() => pdfExportComponent.current?.save()}
          disabled={
            !(
              options.streetName &&
              options.crossStreetStartName &&
              options.crossStreetEndName
            )
          }
        >
          Export to PDF
        </Button>
        <Button
          className={classes.btn}
          startIcon={<SvgIcon icon={logoutIcon} />}
          themeColor="tertiary"
          onClick={() => logout(msalInstance)}
        >
          Log Out
        </Button>
      </div>
      {options.streetName &&
        options.crossStreetStartName &&
        options.crossStreetEndName && (
          <PDFExport
            ref={pdfExportComponent}
            paperSize="A4"
            margin="0.5in"
            scale={0.75}
            fileName={`${getDocumentTitle()}.pdf`}
            forcePageBreak=".page-break"
            keepTogether=".avoid-page-break"
          >
            <div ref={blocksComponent}>
              {streetBlocks.map((x, i) => (
                <StreetBlockInventory key={i} zones={x} pageBreak={i !== 0} />
              ))}
            </div>
          </PDFExport>
        )}
    </>
  ) : (
    <></>
  );
};
